import React, { useState } from "react";
import { toast } from "react-toastify";
import { Request } from "../../../hooks/Request";
import { requestAuth } from "../../../components/services/RequestService";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import { ReportPdfHeader, ReportPdfFooter, formatGeneratedAt } from "./reportBranding";
import {
  addReportBranding,
  styleHeaderRow,
  applyZebraFill,
  finalizeReportSheet,
  addExcelClosingNote,
} from "./reportExcelBranding";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return dateStr.split("T")[0].split("-").reverse().join("/");
  } catch (e) {
    return "-";
  }
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    paddingBottom: 40,
    fontSize: 7,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    flexDirection: "column",
  },
  table: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 3,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#94a3b8",
    minHeight: 24,
    alignItems: "stretch",
  },
  tableRowAlt: { backgroundColor: "#eff6ff" },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#1e3a8a",
    color: "#ffffff",
    alignItems: "center",
    minHeight: 28,
  },
  tableColHeader: {
    paddingHorizontal: 4,
    paddingVertical: 6,
    justifyContent: "center",
    borderRightWidth: 0.5,
    borderRightColor: "#3b5aa8",
  },
  tableCellHeader: { fontSize: 6, fontWeight: "bold", color: "#ffffff", textAlign: "center" },
  tableCol: {
    paddingHorizontal: 4,
    paddingVertical: 6,
    justifyContent: "center",
    borderRightWidth: 0.5,
    borderRightColor: "#e2e8f0",
  },
  tableCell: { fontSize: 6, color: "#334155", textAlign: "center" },
  tableCellRight: { fontSize: 6, color: "#334155", textAlign: "right" },
});

const ReportRitexPDF = ({ data }) => {
  const today = formatGeneratedAt();

  return (
    <Document>
      <Page size="LEGAL" orientation="landscape" style={styles.page}>
        <ReportPdfHeader
          title="Reporte de Operaciones RITEX"
          totalRecords={data.length}
          generatedAt={today}
        />
        <View style={styles.table}>
          <View style={styles.tableHeaderRow} fixed>
            <View style={[styles.tableColHeader, { width: "5%" }]}>
              <Text style={styles.tableCellHeader}>N° IMPORTACION</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "7%" }]}>
              <Text style={styles.tableCellHeader}>PROVEEDOR</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "9%" }]}>
              <Text style={styles.tableCellHeader}>PRODUCTO</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "5%" }]}>
              <Text style={[styles.tableCellHeader, { textAlign: "right" }]}>
                PESO BRUTO (KG)
              </Text>
            </View>
            <View style={[styles.tableColHeader, { width: "6%" }]}>
              <Text style={[styles.tableCellHeader, { textAlign: "right" }]}>
                MONTO TOTAL (USD)
              </Text>
            </View>
            <View style={[styles.tableColHeader, { width: "7%" }]}>
              <Text style={styles.tableCellHeader}>AGENCIA ADUANERA</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "6%" }]}>
              <Text style={styles.tableCellHeader}>FORMULARIO 506</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "7%" }]}>
              <Text style={styles.tableCellHeader}>N° DIM</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "6%" }]}>
              <Text style={styles.tableCellHeader}>FECHA EMISION DIM</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "6%" }]}>
              <Text style={styles.tableCellHeader}>FECHA VENCIMIENTO DIM</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "7%" }]}>
              <Text style={[styles.tableCellHeader, { textAlign: "right" }]}>
                IMPUESTOS ADUANEROS ESTIMADOS (BS)
              </Text>
            </View>
            <View style={[styles.tableColHeader, { width: "6%" }]}>
              <Text style={styles.tableCellHeader}>FECHA CAMBIO DE REGIMEN</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "7%" }]}>
              <Text style={styles.tableCellHeader}>N° DIM REGIMEN A CONSUMO</Text>
            </View>
            <View style={[styles.tableColHeader, { width: "7%" }]}>
              <Text style={styles.tableCellHeader}>
                FORMULARIO 506 CAMBIO DE REGIMEN
              </Text>
            </View>
            <View style={[styles.tableColHeader, { width: "7%" }]}>
              <Text style={[styles.tableCellHeader, { textAlign: "right" }]}>
                IMPUESTOS ACTUALIZADOS (BS)
              </Text>
            </View>
          </View>
          {data.map((row, i) => {
            const isAlt =
              (row._mergeInfo ? row._mergeInfo.groupIdx : i) % 2 !== 0;
            const hideBottomBorder = row._mergeInfo && !row._mergeInfo.isLast;
            const isContinuation = row._mergeInfo && !row._mergeInfo.isFirst;
            const productColStyle = isContinuation
              ? [
                  styles.tableCol,
                  { borderTopWidth: 1, borderTopColor: "#cbd5e1" },
                ]
              : styles.tableCol;

            return (
              <View
                style={[
                  styles.tableRow,
                  isAlt ? styles.tableRowAlt : {},
                  hideBottomBorder ? { borderBottomWidth: 0 } : {},
                ]}
                key={i}
                wrap={false}
              >
                <View style={[styles.tableCol, { width: "5%" }]}>
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row.codigo : ""}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: "7%" }]}>
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row.proveedor : ""}
                  </Text>
                </View>
                <View style={[productColStyle, { width: "9%" }]}>
                  <Text style={styles.tableCell}>{row.producto}</Text>
                </View>
                <View style={[productColStyle, { width: "5%" }]}>
                  <Text style={styles.tableCellRight}>{row.totalKg}</Text>
                </View>
                <View style={[productColStyle, { width: "6%" }]}>
                  <Text style={styles.tableCellRight}>{row.totalUsd}</Text>
                </View>
                <View style={[styles.tableCol, { width: "7%" }]}>
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row.agenciaAduanera : ""}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: "6%" }]}>
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row.formulario506 : ""}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: "7%" }]}>
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row.numeroDui : ""}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: "6%" }]}>
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row.fechaDim : ""}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: "6%" }]}>
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row.fechaVencimiento : ""}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: "7%" }]}>
                  <Text style={styles.tableCellRight}>
                    {!isContinuation ? row.impuestosRitex : ""}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: "6%" }]}>
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row.fechaNacionalizacion : ""}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: "7%" }]}>
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row.numeroDimRegimen : ""}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: "7%" }]}>
                  <Text style={styles.tableCell}>
                    {!isContinuation ? row.formulario506Reemplazo : ""}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: "7%" }]}>
                  <Text style={styles.tableCellRight}>
                    {!isContinuation ? row.impuestosNacionalizacion : ""}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
        <ReportPdfFooter />
      </Page>
    </Document>
  );
};

export const ReportRitexModal = ({ onClose }) => {
  const [filters, setFilters] = useState({
    nroOperacion: "",
    proveedor: "",
    agenciaAduanera: "",
    tipoImportacion: "",
    estadoNacionalizacion: "",
  });
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const { data: typesImport } = Request({
    urlApi: "/Dropdown/types-import/options?PageNumber=1",
  });
  const { data: providers } = Request({
    urlApi: "/Dropdown/providers/options?PageNumber=1",
  });

  const handleGenerate = async () => {
    setLoading(true);

    const getArray = (item) => {
      let arr = item.formularioRitex || item.itemsRitex || item.detalleRitex;
      if (typeof arr === "string") {
        try {
          arr = JSON.parse(arr);
        } catch (e) {
          arr = [];
        }
      }
      return Array.isArray(arr) && arr.length > 0 ? arr : [];
    };

    const getDetail = (subItem) => {
      try {
        return subItem.description2 ? JSON.parse(subItem.description2) : {};
      } catch (e) {
        return {};
      }
    };

    try {
      const url = `/Procedure/pag?Fieldnames=codigoDeInterno&Fieldnames=nroPedido&Fieldnames=proveedor&Fieldnames=descripciónGeneralDeLaCompra
&Fieldnames=marcaRitex&Fieldnames=codigoRitex&Fieldnames=total(Kg)Ritex&Fieldnames=precioUnitarioUsdRitex&Fieldnames=totalUsdRitex&Fieldnames=precioUnitarioEuRitex&Fieldnames=totalEurosRitex&Fieldnames=agenciaAduaneraEncargadaRitex&Fieldnames=formulario506Ritex&Fieldnames=númeroDeDuiRitex&Fieldnames=fechaDeDimRitex&Fieldnames=fechaVencimientoRitex&Fieldnames=fechaNacionalizacionRitex&Fieldnames=numeroDimRegimenAConsumoRitex&Fieldnames=numeroDeDimRegimenAConsumoRitex&Fieldnames=formulario506ReemplazadoRitex&Fieldnames=pagoDuiEstimadoEnBs_Ritex&Fieldnames=tipoDeImportacion&Fieldnames=agenteDespachante&Fieldnames=dimNumber&Fieldnames=fechaNacionalizacion&Fieldnames=formulario506&Fieldnames=pagoDuiEstimadoEnBs&Fieldnames=numeroDimRegimenAConsumo&Fieldnames=formulario506Reemplazado&Fieldnames=fechaDeDim&Fieldnames=formularioRitex&Fieldnames=itemsRitex&&Fieldnames=impuestosActualizados(Bs)&Fieldnames=detalleRitex&IsPaginado=false`;

      const response = await requestAuth("get", url);
      let data = response.data?.data || response.data || [];

      // Filtrar localmente
      if (filters.nroOperacion) {
        data = data.filter((item) =>
          (item.nroPedido || item.codigoDeInterno)
            ?.toLowerCase()
            .includes(filters.nroOperacion.toLowerCase()),
        );
      }
      if (filters.tipoImportacion) {
        data = data.filter(
          (item) => item.tipoDeImportacion === filters.tipoImportacion,
        );
      }
      if (filters.proveedor) {
        data = data.filter((item) => item.proveedor === filters.proveedor);
      }
      if (filters.agenciaAduanera) {
        const search = filters.agenciaAduanera.toLowerCase();
        data = data.filter((item) => {
          let matches = false;
          const checkAgencia = (ag) => {
            if (typeof ag === "object" && ag !== null) {
              ag = ag.name || ag.label || JSON.stringify(ag);
            }
            if (ag && ag.toLowerCase().includes(search)) matches = true;
          };

          checkAgencia(
            item.agenciaAduaneraEncargadaRitex || item.agenteDespachante,
          );

          getArray(item).forEach((subItem) => {
            const detail = getDetail(subItem);
            checkAgencia(
              detail.agenciaAduaneraEncargadaRitex ||
                detail["AGENCIA ADUANERA ENCARGADA RITEX"] ||
                subItem.agenciaAduaneraEncargadaRitex,
            );
          });

          return matches;
        });
      }
      if (filters.estadoNacionalizacion) {
        data = data.filter((item) => {
          let hasDate =
            (item.fechaNacionalizacionRitex &&
              item.fechaNacionalizacionRitex.trim() !== "") ||
            (item.fechaNacionalizacion &&
              item.fechaNacionalizacion.trim() !== "");

          if (!hasDate) {
            getArray(item).forEach((subItem) => {
              const detail = getDetail(subItem);
              if (
                detail.fechaNacionalizacionRitex ||
                detail["FECHA NACIONALIZACION RITEX"] ||
                subItem.fechaNacionalizacionRitex
              ) {
                hasDate = true;
              }
            });
          }

          if (filters.estadoNacionalizacion === "con") return hasDate;
          if (filters.estadoNacionalizacion === "sin") return !hasDate;
          return true;
        });
      }

      setReportData(data);
      toast.success("Reporte generado exitosamente");
    } catch (error) {
      console.error(error);
      toast.error("Error al generar el reporte");
    } finally {
      setLoading(false);
    }
  };

  const getFlattenedData = () => {
    const flattened = [];
    let groupIdx = 0;

    reportData.forEach((item) => {
      let itemsArray =
        item.formularioRitex || item.itemsRitex || item.detalleRitex;
      if (typeof itemsArray === "string") {
        try {
          itemsArray = JSON.parse(itemsArray);
        } catch (e) {
          itemsArray = [];
        }
      }
      if (!Array.isArray(itemsArray) || itemsArray.length === 0) {
        itemsArray = [item];
      }

      itemsArray.forEach((subItem, i) => {
        let detail = {};
        try {
          detail = subItem.description2 ? JSON.parse(subItem.description2) : {};
        } catch (e) {}

        const getVal = (keys, fallback) => {
          for (let k of keys) {
            if (
              detail[k] !== undefined &&
              detail[k] !== null &&
              detail[k] !== ""
            )
              return detail[k];
            if (
              subItem[k] !== undefined &&
              subItem[k] !== null &&
              subItem[k] !== ""
            )
              return subItem[k];
            if (item[k] !== undefined && item[k] !== null && item[k] !== "")
              return item[k];
          }
          if (fallback !== undefined && fallback !== null && fallback !== "")
            return fallback;
          return "-";
        };

        let agencia = getVal(
          [
            "agenciaAduaneraEncargadaRitex",
            "AGENCIA ADUANERA ENCARGADA RITEX",
            "agenteDespachante",
          ],
          "-",
        );
        if (typeof agencia === "object" && agencia !== null) {
          agencia = agencia.name || agencia.label || JSON.stringify(agencia);
        }

        flattened.push({
          codigo: i === 0 ? item.nroPedido || item.codigoDeInterno || "-" : "",
          proveedor: i === 0 ? item.proveedor || "-" : "",
          producto: getVal([
            "descripciónGeneralDeLaCompra"
          ]),
          marca: getVal(["marcaRitex", "Marca RITEX", "MARCA RITEX", "marca"]),
          codigoRitex: getVal([
            "codigoRitex",
            "Código RITEX",
            "CÓDIGO RITEX",
            "codigo",
          ]),
          totalKg: getVal([
            "total(Kg)Ritex",
            "totalKgRitex",
            "Total (KG) RITEX",
            "TOTAL (KG) RITEX",
            "totalKg",
            "cantidad",
          ]),
          pUnitUsd: getVal([
            "precioUnitarioUsdRitex",
            "Precio Unitario USD RITEX",
            "PRECIO UNITARIO USD RITEX",
            "precioUnitarioUsd",
            "pUnitUsd",
          ]),
          totalUsd: getVal([
            "totalUsdRitex",
            "Total USD RITEX",
            "TOTAL USD RITEX",
            "totalUsd",
          ]),
          pUnitEu: getVal([
            "precioUnitarioEuRitex",
            "Precio Unitario EU RITEX",
            "PRECIO UNITARIO EU RITEX",
            "precioUnitarioEu",
            "pUnitEu",
          ]),
          totalEuros: getVal([
            "totalEurosRitex",
            "Total Euros RITEX",
            "TOTAL EUROS RITEX",
            "totalEuros",
          ]),
          agenciaAduanera: agencia,
          formulario506: getVal([
            "formulario506Ritex",
            "Formulario 506 RITEX",
            "FORMULARIO 506 RITEX",
            "formulario506",
          ]),
          numeroDui: getVal([
            "númeroDeDuiRitex",
            "numeroDeDuiRitex",
            "Número de DUI RITEX",
            "NÚMERO DE DUI RITEX",
            "numeroDui",
            "dimNumber",
          ]),
          fechaDim: formatDate(
            getVal([
              "fechaDeDimRitex",
              "Fecha de DIM RITEX",
              "FECHA DE DIM RITEX",
              "fechaDeDim",
            ]),
          ),
          fechaVencimiento: formatDate(
            getVal([
              "fechaVencimientoRitex",
              "Fecha Vencimiento RITEX",
              "FECHA VENCIMIENTO RITEX",
              "fechaVencimiento",
            ]),
          ),
          impuestosRitex: getVal([
            "pagoDuiEstimadoEnBs_Ritex",
            "pagoDuiEstimadoEnBsRitex",
            "Pago DUI Estimado en BS. RITEX",
            "PAGO DUI ESTIMADO EN BS. RITEX",
          ]),
          fechaNacionalizacion: formatDate(
            getVal([
              "fechaNacionalizacionRitex",
              "Fecha Nacionalizacion RITEX",
              "FECHA NACIONALIZACION RITEX",
              "fechaNacionalizacion",
            ]),
          ),
          numeroDimRegimen: getVal([
            "numeroDimRegimenAConsumoRitex",
            "numeroDeDimRegimenAConsumoRitex",
            "Numero de DIM Regimen a Consumo RITEX",
            "NUMERO DE DIM REGIMEN A CONSUMO RITEX",
            "numeroDimRegimenAConsumo",
          ]),
          formulario506Reemplazo: getVal([
            "formulario506ReemplazadoRitex",
            "Formulario 506 Reemplazado RITEX",
            "FORMULARIO 506 REEMPLAZADO RITEX",
            "formulario506Reemplazado",
          ]),
          impuestosNacionalizacion: getVal([
            "impuestosActualizados(Bs)",
            "Pago DUI Estimado en BS.",
            "PAGO DUI ESTIMADO EN BS.",
          ]),
          _mergeInfo: {
            rowspan: i === 0 ? itemsArray.length : 0,
            isFirst: i === 0,
            isLast: i === itemsArray.length - 1,
            groupIdx,
          },
        });
      });
      groupIdx++;
    });
    return flattened;
  };

  const handleDownloadExcel = async () => {
    if (!reportData || reportData.length === 0) {
      toast.warning("No hay datos para exportar");
      return;
    }
    const data = getFlattenedData();
    const generatedAt = formatGeneratedAt();

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("REPORTE_DE_RITEX");

    const COLUMN_DEFS = [
      { header: "N° IMPORTACION", key: "codigo", width: 20 },
      { header: "PROVEEDOR", key: "proveedor", width: 20 },
      { header: "PRODUCTO", key: "producto", width: 25 },
      { header: "PESO BRUTO (KG)", key: "totalKg", width: 15 },
      { header: "MONTO TOTAL (USD)", key: "totalUsd", width: 18 },
      { header: "AGENCIA ADUANERA", key: "agenciaAduanera", width: 25 },
      { header: "FORMULARIO 506", key: "formulario506", width: 15 },
      { header: "N° DIM", key: "numeroDui", width: 15 },
      { header: "FECHA EMISION DIM", key: "fechaDim", width: 18 },
      { header: "FECHA VENCIMIENTO DIM", key: "fechaVencimiento", width: 22 },
      { header: "IMPUESTOS ADUANEROS ESTIMADOS (BS)", key: "impuestosRitex", width: 35 },
      { header: "FECHA CAMBIO DE REGIMEN", key: "fechaNacionalizacion", width: 22 },
      { header: "N° DIM REGIMEN A CONSUMO", key: "numeroDimRegimen", width: 25 },
      { header: "FORMULARIO 506 CAMBIO DE REGIMEN", key: "formulario506Reemplazo", width: 32 },
      { header: "IMPUESTOS ACTUALIZADOS (BS)", key: "impuestosNacionalizacion", width: 28 },
    ];

    worksheet.columns = COLUMN_DEFS.map(({ header, ...rest }) => rest);

    const headerRow = await addReportBranding(workbook, worksheet, {
      title: "Reporte de Operaciones RITEX",
      colCount: COLUMN_DEFS.length,
      totalRecords: data.length,
      generatedAt,
    });

    worksheet.getRow(headerRow).values = COLUMN_DEFS.map((c) => c.header);
    styleHeaderRow(worksheet.getRow(headerRow), undefined, 40);

    let rowIndex = headerRow + 1;

    data.forEach((row) => {
      const isAlt = (row._mergeInfo ? row._mergeInfo.groupIdx : rowIndex) % 2 !== 0;
      const excelRow = worksheet.addRow({
        codigo: row.codigo,
        proveedor: row.proveedor,
        producto: row.producto,
        totalKg: row.totalKg,
        totalUsd: row.totalUsd,
        agenciaAduanera: row.agenciaAduanera,
        formulario506: row.formulario506,
        numeroDui: row.numeroDui,
        fechaDim: row.fechaDim,
        fechaVencimiento: row.fechaVencimiento,
        impuestosRitex: row.impuestosRitex,
        fechaNacionalizacion: row.fechaNacionalizacion,
        numeroDimRegimen: row.numeroDimRegimen,
        formulario506Reemplazo: row.formulario506Reemplazo,
        impuestosNacionalizacion: row.impuestosNacionalizacion,
      });
      applyZebraFill(excelRow, isAlt);

      // Combinar (Merge) celdas para agrupar
      if (
        row._mergeInfo &&
        row._mergeInfo.isFirst &&
        row._mergeInfo.rowspan > 1
      ) {
        const span = row._mergeInfo.rowspan;
        const columnsToMerge = ["A", "B", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O"];
        columnsToMerge.forEach((col) => {
          worksheet.mergeCells(
            `${col}${rowIndex}:${col}${rowIndex + span - 1}`,
          );
          worksheet.getCell(`${col}${rowIndex}`).alignment = {
            vertical: "middle",
            horizontal: "left",
            wrapText: true,
          };
        });
      }
      rowIndex++;
    });

    const lastDataRow = rowIndex - 1;
    finalizeReportSheet(worksheet, {
      headerTopRow: headerRow,
      lastDataRow,
      colCount: COLUMN_DEFS.length,
    });
    addExcelClosingNote(worksheet, {
      row: lastDataRow + 2,
      colCount: COLUMN_DEFS.length,
      generatedAt,
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "REPORTE_DE_RITEX.xlsx");
    toast.success("Excel descargado exitosamente");
  };

  const handleDownloadPDF = async () => {
    if (!reportData || reportData.length === 0) {
      toast.warning("No hay datos para exportar");
      return;
    }
    toast.info("Generando PDF, por favor espere...");
    try {
      const data = getFlattenedData();
      const doc = <ReportRitexPDF data={data} />;
      const asPdf = pdf([]);
      asPdf.updateContainer(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "REPORTE_DE_RITEX.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF descargado exitosamente");
    } catch (error) {
      console.error(error);
      toast.error("Error al generar PDF");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-2xl w-[98vw] max-w-[1500px] max-h-[90vh] flex flex-col relative overflow-hidden uppercase">
      <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white flex justify-between items-center shrink-0">
        <h2 className="text-lg md:text-xl font-bold text-gray-800 m-0">
          REPORTE DE OPERACIONES RITEX
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          <i className="fas fa-times text-xl"></i>
        </button>
      </div>

      <div className="p-4 md:p-6 overflow-y-auto flex-1 bg-white">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código / Nro Operación
            </label>
            <input
              type="text"
              placeholder="Buscar..."
              value={filters.nroOperacion}
              onChange={(e) =>
                setFilters({ ...filters, nroOperacion: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Importación
            </label>
            <select
              value={filters.tipoImportacion}
              onChange={(e) =>
                setFilters({ ...filters, tipoImportacion: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 transition-colors bg-white"
            >
              <option value="">Todos</option>
              {typesImport?.map((t, i) => (
                <option key={i} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proveedor
            </label>
            <select
              value={filters.proveedor}
              onChange={(e) =>
                setFilters({ ...filters, proveedor: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 transition-colors bg-white"
            >
              <option value="">Todos</option>
              {providers?.map((p, i) => (
                <option key={i} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Agencia Aduanera
            </label>
            <input
              type="text"
              placeholder="Buscar..."
              value={filters.agenciaAduanera}
              onChange={(e) =>
                setFilters({ ...filters, agenciaAduanera: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado Nacionalización
            </label>
            <select
              value={filters.estadoNacionalizacion}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  estadoNacionalizacion: e.target.value,
                })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 transition-colors bg-white"
            >
              <option value="">Todos</option>
              <option value="con">Con Fecha (Nacionalizados)</option>
              <option value="sin">Sin Fecha (Pendientes)</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 mt-4 border-b border-gray-200 pb-4">
          {/*
            <button
              className={`px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2 ${!reportData ? "bg-green-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
              onClick={handleDownloadExcel}
              disabled={!reportData || loading}
            >
              <i className="fas fa-file-excel"></i>
              <span>Descargar Excel</span>
            </button>
          */}
          <button
            className={`px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2 ${!reportData ? "bg-red-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
            onClick={handleDownloadPDF}
            disabled={!reportData || loading}
          >
            <i className="fas fa-file-pdf"></i>
            <span>Descargar PDF</span>
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            onClick={handleGenerate}
            disabled={loading}
          >
            <i
              className={
                loading ? "fas fa-spinner fa-spin mr-2" : "fas fa-eye mr-2"
              }
            ></i>
            <span>{loading ? "Cargando..." : "Previsualizar"}</span>
          </button>
        </div>

        {reportData && (
          <>
            <div className="flex flex-wrap gap-4 mt-4 mb-4">
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-md flex-1 text-center min-w-[150px]">
                <span className="block text-xs text-blue-600 font-semibold uppercase tracking-wider mb-1">
                  Total Registros
                </span>
                <span className="text-2xl font-bold text-blue-800">
                  {reportData.length}
                </span>
              </div>
              <div className="bg-green-50 border border-green-200 p-3 rounded-md flex-1 text-center min-w-[150px]">
                <span className="block text-xs text-green-600 font-semibold uppercase tracking-wider mb-1">
                  Nacionalizados
                </span>
                <span className="text-2xl font-bold text-green-800">
                  {
                    reportData.filter(
                      (item) =>
                        (item.fechaNacionalizacionRitex &&
                          item.fechaNacionalizacionRitex.trim() !== "") ||
                        (item.fechaNacionalizacion &&
                          item.fechaNacionalizacion.trim() !== ""),
                    ).length
                  }
                </span>
              </div>
              <div className="bg-orange-50 border border-orange-200 p-3 rounded-md flex-1 text-center min-w-[150px]">
                <span className="block text-xs text-orange-600 font-semibold uppercase tracking-wider mb-1">
                  Pendientes
                </span>
                <span className="text-2xl font-bold text-orange-800">
                  {
                    reportData.filter(
                      (item) =>
                        (!item.fechaNacionalizacionRitex ||
                          item.fechaNacionalizacionRitex.trim() === "") &&
                        (!item.fechaNacionalizacion ||
                          item.fechaNacionalizacion.trim() === ""),
                    ).length
                  }
                </span>
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 rounded-md shadow-sm">
              <table className="w-full text-xs text-left">
                <thead className="bg-[#1e3a8a] text-white font-bold border-b border-gray-300 shadow-sm">
                  <tr>
                    <th className="px-3 py-2 whitespace-nowrap">N° IMPORTACION</th>
                    <th className="px-3 py-2 whitespace-nowrap">PROVEEDOR</th>
                    <th className="px-3 py-2 min-w-[150px] whitespace-nowrap">PRODUCTO</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">PESO BRUTO (KG)</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">MONTO TOTAL (USD)</th>
                    <th className="px-3 py-2 whitespace-nowrap">AGENCIA ADUANERA</th>
                    <th className="px-3 py-2 whitespace-nowrap">FORMULARIO 506</th>
                    <th className="px-3 py-2 whitespace-nowrap">N° DIM</th>
                    <th className="px-3 py-2 whitespace-nowrap">FECHA EMISION DIM</th>
                    <th className="px-3 py-2 whitespace-nowrap">FECHA VENCIMIENTO DIM</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">IMPUESTOS ADUANEROS ESTIMADOS (BS)</th>
                    <th className="px-3 py-2 whitespace-nowrap">FECHA CAMBIO DE REGIMEN</th>
                    <th className="px-3 py-2 whitespace-nowrap">N° DIM REGIMEN A CONSUMO</th>
                    <th className="px-3 py-2 whitespace-nowrap">FORMULARIO 506 CAMBIO DE REGIMEN</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">IMPUESTOS ACTUALIZADOS (BS)</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.length === 0 ? (
                    <tr>
                      <td
                        colSpan="15"
                        className="text-center py-6 text-gray-500 text-sm"
                      >
                        No se encontraron resultados para los filtros
                        seleccionados.
                      </td>
                    </tr>
                  ) : (
                    getFlattenedData().map((row, idx) => {
                      const isFirst = row._mergeInfo
                        ? row._mergeInfo.isFirst
                        : true;
                      const rowspan = row._mergeInfo
                        ? row._mergeInfo.rowspan
                        : 1;
                      return (
                        <tr
                          key={idx}
                          className={`border-b border-gray-200 align-middle transition-colors ${
                            row._mergeInfo && row._mergeInfo.groupIdx % 2 === 0
                              ? "bg-white"
                              : "bg-blue-50/50"
                          } hover:bg-blue-100/50`}
                        >
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 font-medium text-gray-900 border-r border-gray-200"
                            >
                              {row.codigo}
                            </td>
                          )}
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 border-r border-gray-200"
                            >
                              {row.proveedor}
                            </td>
                          )}
                          <td className="px-3 py-2 border-r border-gray-100">
                            <div
                              className="max-w-[200px] truncate"
                              title={row.producto}
                            >
                              {row.producto}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right border-r border-gray-100">
                            {row.totalKg !== 0 ? row.totalKg : "-"}
                          </td>
                          <td className="px-3 py-2 text-right border-r border-gray-200">
                            {row.totalUsd !== 0 ? row.totalUsd : "-"}
                          </td>
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 border-r border-gray-200"
                            >
                              {row.agenciaAduanera}
                            </td>
                          )}
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 border-r border-gray-200"
                            >
                              {row.formulario506}
                            </td>
                          )}
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 border-r border-gray-200"
                            >
                              {row.numeroDui}
                            </td>
                          )}
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 border-r border-gray-200"
                            >
                              {row.fechaDim}
                            </td>
                          )}
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 border-r border-gray-200"
                            >
                              {row.fechaVencimiento}
                            </td>
                          )}
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 border-r border-gray-200 text-right"
                            >
                              {row.impuestosRitex}
                            </td>
                          )}
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 border-r border-gray-200"
                            >
                              {row.fechaNacionalizacion}
                            </td>
                          )}
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 border-r border-gray-200"
                            >
                              {row.numeroDimRegimen}
                            </td>
                          )}
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 border-r border-gray-200"
                            >
                              {row.formulario506Reemplazo}
                            </td>
                          )}
                          {isFirst && (
                            <td
                              rowSpan={rowspan}
                              className="px-3 py-2 text-right"
                            >
                              {row.impuestosNacionalizacion}
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="px-4 py-3 md:px-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 shrink-0">
        <button
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-medium shadow-sm"
          onClick={onClose}
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};
